import { z } from "zod";
import type { Operation } from "../types";

/**
 * Agent browser QA (docs/qa/AGENT-BROWSER-QA-PRD.md §5.9) — the wire contract
 * for /agents/qa.
 *
 * Runs are read from the artifact bundles qaRun already writes to
 * ~/.excellent/qa-runs/, so the UI and an agent's `browser_qa run` see exactly
 * the same evidence; there is no parallel store to drift.
 */

export const qaStepRecord = z.object({
  step: z.number().int(),
  op: z.string(),
  ok: z.boolean(),
  ms: z.number(),
  url: z.string().optional(),
  title: z.string().optional(),
  reason: z.string().optional(),
});
export type QaStepRecordDTO = z.infer<typeof qaStepRecord>;

export const qaRunSummary = z.object({
  runId: z.string(),
  label: z.string(),
  ok: z.boolean(),
  startedAt: z.number(),
  steps: z.number().int(),
  failedAt: z.number().int().nullable(),
  reason: z.string().nullable(),
  ms: z.number(),
  hasScreenshot: z.boolean(),
});
export type QaRunSummaryDTO = z.infer<typeof qaRunSummary>;

export const qaRunDetail = qaRunSummary.extend({
  results: z.array(qaStepRecord),
  console: z.array(z.object({ type: z.string(), text: z.string(), ts: z.number() })),
  networkFailures: z.array(
    z.object({ url: z.string(), errorText: z.string(), type: z.string(), ts: z.number() }),
  ),
  outline: z.array(z.object({ role: z.string(), name: z.string(), tag: z.string() })),
  artifacts: z.array(z.string()),
});
export type QaRunDetailDTO = z.infer<typeof qaRunDetail>;

export const qaSuite = z.object({
  slug: z.string(),
  name: z.string(),
  description: z.string().optional(),
  steps: z.array(z.record(z.string(), z.unknown())),
  destructive: z.boolean().optional(),
  /** RO-6-07: suite opts into the vault.query read escape hatch. */
  allowVaultRead: z.boolean().optional(),
  updatedAt: z.number(),
});
export type QaSuiteDTO = z.infer<typeof qaSuite>;

export const qaRunsList: Operation<
  void,
  { limit?: number },
  void,
  { runs: QaRunSummaryDTO[] }
> = {
  method: "GET",
  path: () => "/api/qa/runs",
  query: z.object({ limit: z.coerce.number().int().positive().max(500).optional() }),
  response: z.object({ runs: z.array(qaRunSummary) }),
};

export const qaRunGet: Operation<
  { runId: string },
  void,
  void,
  { run: QaRunDetailDTO }
> = {
  method: "GET",
  path: ({ runId }) => `/api/qa/runs/${encodeURIComponent(runId)}`,
  params: z.object({ runId: z.string() }),
  response: z.object({ run: qaRunDetail }),
};

/**
 * Raw artifact bytes (screenshots, JSON). Served as-is rather than through the
 * {ok,data} envelope — an <img src> needs the image, not a wrapper.
 */
// coverage-ignore: artifact GET Hono port deferred after RO-10-09; sibling QA ops mounted
export const qaArtifactGet: Operation<{ runId: string; name: string }, void, void, unknown> = {
  method: "GET",
  path: ({ runId, name }) =>
    `/api/qa/runs/${encodeURIComponent(runId)}/artifact/${encodeURIComponent(name)}`,
  params: z.object({ runId: z.string(), name: z.string() }),
  response: z.unknown(),
};

export const qaSuitesList: Operation<void, void, void, { suites: QaSuiteDTO[] }> = {
  method: "GET",
  path: () => "/api/qa/suites",
  response: z.object({ suites: z.array(qaSuite) }),
};

export const qaSuiteSave: Operation<
  void,
  void,
  {
    slug: string;
    name: string;
    description?: string;
    steps: Array<Record<string, unknown>>;
    destructive?: boolean;
    allowVaultRead?: boolean;
  },
  { suite: QaSuiteDTO }
> = {
  method: "POST",
  path: () => "/api/qa/suites",
  body: z.object({
    slug: z
      .string()
      .regex(/^[a-z0-9-]+$/, "slug must be lowercase letters, digits and dashes"),
    name: z.string().min(1),
    description: z.string().optional(),
    steps: z.array(z.record(z.string(), z.unknown())).min(1),
    destructive: z.boolean().optional(),
    allowVaultRead: z.boolean().optional(),
  }),
  response: z.object({ suite: qaSuite }),
};

/** Execute a suite now. Returns the run id so the UI can jump to the detail. */
export const qaSuiteRun: Operation<
  void,
  void,
  { slug: string; baseUrl?: string; viewport?: string },
  { runId: string; ok: boolean; reason: string | null; failedAt: number | null }
> = {
  method: "POST",
  path: () => "/api/qa/run",
  body: z.object({
    slug: z.string().regex(/^[a-z0-9-]+$/),
    baseUrl: z.string().optional(),
    viewport: z.string().optional(),
  }),
  response: z.object({
    runId: z.string(),
    ok: z.boolean(),
    reason: z.string().nullable(),
    failedAt: z.number().int().nullable(),
  }),
};

export const qa = {
  runsList: qaRunsList,
  runGet: qaRunGet,
  suitesList: qaSuitesList,
  suiteSave: qaSuiteSave,
  suiteRun: qaSuiteRun,
};
